import { useMemo } from 'react';
import { ContextLinkDefinition, parseTemplateVariables } from '../../core';
import { ContextLinkConfig } from './panels';

export const SCOPE_PREFIX = '__data';
// This is the scope prefix that we initially used for series links.
// It does not show up in the UI anymore, but we still need to support it for backwards compatibility.
export const DEPRECATED_SCOPE_PREFIX = '__series';

export type LinkDataContext = {
  name?: string;
  labels?: Record<string, string>;
  timestamp?: number;
  value?: number | string | null | boolean;
};

/*
  Transform linkDataContext into a flat object with keys that can be used as variables
  in a template string.

  @param context - The context to transform
  @param prefix - The prefix to use for the variables (e.g. __data will result in __data.name etc.)
*/
export function getLinkDataContextVariables(context: LinkDataContext, prefix: string): Record<string, string> {
  const flattened = {} as Record<string, string>;

  if (context.name) {
    flattened[`${prefix}.name`] = context.name;
  }

  if (context.timestamp) {
    flattened[`${prefix}.timestamp`] = `${context.timestamp}`;
  }

  if (context.value !== undefined) {
    flattened[`${prefix}.value`] = `${context.value}`;
  }

  if (context.labels) {
    for (const [key, value] of Object.entries(context.labels)) {
      flattened[`${prefix}.labels.${key}`] = value;
    }
  }

  return flattened;
}

export type ContextLinkType = 'Series' | 'Panel';

export function isScopedVariable(variable: string, contextLinkConfig?: ContextLinkConfig): boolean {
  if (!contextLinkConfig) return false;

  let isScoped = variable.trim().startsWith(SCOPE_PREFIX);
  if (!isScoped) {
    isScoped = variable.trim().startsWith(DEPRECATED_SCOPE_PREFIX);
  }

  return isScoped;
}

export function getVariableScope(variable: string, contextLinkConfig?: ContextLinkConfig): string {
  if (!contextLinkConfig) return 'Panel';

  return isScopedVariable(variable, contextLinkConfig) ? contextLinkConfig.scopeName : 'Panel';
}

/**
 * Helper to determine if a context link is a series link or a panel link.
 */
export function getContextLinkType(url: string, contextLinkConfig?: ContextLinkConfig): string {
  if (!contextLinkConfig) return 'Panel';

  const variables = parseTemplateVariables(url);
  // If a variable starts with series. then it's a series link
  const isScoped = variables.some((v) => {
    return isScopedVariable(v, contextLinkConfig);
  });
  return isScoped ? contextLinkConfig.scopeName : 'Panel';
}

function getScopedLinks(links?: ContextLinkDefinition[], contextLinkConfig?: ContextLinkConfig) {
  return links?.filter((l) => getContextLinkType(l.spec.url, contextLinkConfig) === contextLinkConfig?.scopeName) ?? [];
}

function getPanelLinks(links?: ContextLinkDefinition[], contextLinkConfig?: ContextLinkConfig) {
  return links?.filter((l) => getContextLinkType(l.spec.url, contextLinkConfig) === 'Panel') ?? [];
}

/**
 * Helper hook to get the scoped and panel links from a list of context links.
 */
export function useComputeContextLinkType(links?: ContextLinkDefinition[], contextLinkConfig?: ContextLinkConfig) {
  return useMemo(() => {
    return {
      scopedLinks: getScopedLinks(links, contextLinkConfig),
      panelLinks: getPanelLinks(links, contextLinkConfig),
    };
  }, [links, contextLinkConfig]);
}
